import { useWindowManager, WindowType } from '@/contexts/WindowManagerContext';
import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';

// Lazy load modal components with correct exports
const InvoiceModal = lazy(() => import('@/components/modals/InvoiceModal'));
const CustomerModal = lazy(() => import('@/components/modals/CustomerModal').then(m => ({ default: m.CustomerModal })));
const VendorModal = lazy(() => import('@/components/modals/VendorModal').then(m => ({ default: m.VendorModal })));
const ProductModal = lazy(() => import('@/components/inventory/ProductModal').then(m => ({ default: m.ProductModal })));
const EnhancedBulkUploadModal = lazy(() => import('@/components/modals/EnhancedBulkUploadModal').then(m => ({ default: m.EnhancedBulkUploadModal })));
const LedgerEntryModal = lazy(() => import('@/components/modals/LedgerEntryModal'));
const ReturnInvoiceModal = lazy(() => import('@/components/modals/ReturnInvoiceModal'));
const CategoryModal = lazy(() => import('@/components/modals/CategoryModal').then(m => ({ default: m.CategoryModal })));
const CustomerGroupModal = lazy(() => import('@/components/modals/CustomerGroupModal').then(m => ({ default: m.CustomerGroupModal })));
const VendorGroupModal = lazy(() => import('@/components/modals/VendorGroupModal').then(m => ({ default: m.VendorGroupModal })));

const LoadingFallback = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

export const WindowRenderer = () => {
  const { windows, closeWindow, minimizeWindow, updateWindowProgress, updateWindowTitle } = useWindowManager();

  const renderWindowContent = (windowId: string, type: WindowType, props: Record<string, any>, isMinimized: boolean) => {
    // Minimize instead of close when clicking outside
    const handleMinimizeOnClose = () => minimizeWindow(windowId);
    
    // Actual close - called by close button
    const handleActualClose = () => closeWindow(windowId);
    
    const handleSuccess = () => {
      props.onSuccess?.();
      closeWindow(windowId);
    };

    // Progress update handler for bulk uploads
    const handleProgressUpdate = (progress: { isLoading: boolean; progressPercent?: number; progressLabel?: string }) => {
      updateWindowProgress(windowId, progress);
    };

    const handleTitleUpdate = (title: string) => {
      updateWindowTitle(windowId, title);
    };

    switch (type) {
      case 'invoice':
        return (
          <InvoiceModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            onSuccess={handleSuccess}
            type={props.invoiceType || 'sale'}
            customerId={props.customerId}
            vendorId={props.vendorId}
            editInvoiceId={props.editInvoiceId}
            windowId={windowId}
          />
        );
      
      case 'customer':
        return (
          <CustomerModal 
            open={true}
            isMinimized={isMinimized}
            onOpenChange={(open) => !open && handleActualClose()}
            onMinimize={handleMinimizeOnClose}
            onClose={handleActualClose}
            organizationId={props.organizationId}
            customer={props.customer}
            onSuccess={handleSuccess}
            windowId={windowId}
          />
        );
      
      case 'vendor':
        return (
          <VendorModal 
            open={true}
            isMinimized={isMinimized}
            onOpenChange={(open) => !open && handleActualClose()}
            onMinimize={handleMinimizeOnClose}
            onClose={handleActualClose}
            organizationId={props.organizationId}
            vendor={props.vendor}
            onSuccess={handleSuccess}
            windowId={windowId}
          />
        );
      
      case 'product':
        return (
          <ProductModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            organizationId={props.organizationId || ''}
            product={props.product}
            onSuccess={handleSuccess}
            windowId={windowId}
          />
        );
      
      case 'bulk-upload-products':
        return (
          <EnhancedBulkUploadModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            type="products"
            organizationId={props.organizationId || ''}
            onSuccess={handleSuccess}
            onProgressUpdate={handleProgressUpdate}
            onTitleUpdate={handleTitleUpdate}
            windowId={windowId}
          />
        );
      
      case 'bulk-upload-customers':
        return (
          <EnhancedBulkUploadModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            type="customers"
            organizationId={props.organizationId || ''}
            onSuccess={handleSuccess}
            onProgressUpdate={handleProgressUpdate}
            onTitleUpdate={handleTitleUpdate}
            windowId={windowId}
          />
        );
      
      case 'bulk-upload-vendors':
        return (
          <EnhancedBulkUploadModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            type="vendors"
            organizationId={props.organizationId || ''}
            onSuccess={handleSuccess}
            onProgressUpdate={handleProgressUpdate}
            onTitleUpdate={handleTitleUpdate}
            windowId={windowId}
          />
        );
      
      case 'ledger-entry':
        return (
          <LedgerEntryModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            onSuccess={handleSuccess}
            partyId={props.partyId || ''}
            partyType={props.partyType || 'customer'}
            entry={props.entry}
            windowId={windowId}
          />
        );
      
      case 'return-invoice':
        return (
          <ReturnInvoiceModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            onSuccess={handleSuccess}
            type={props.returnType || 'sale_return'}
            customerId={props.customerId}
            vendorId={props.vendorId}
            organizationId={props.organizationId || ''}
            editInvoiceId={props.editInvoiceId}
            windowId={windowId}
          />
        );
      
      case 'category':
        return (
          <CategoryModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            onSuccess={handleSuccess}
            organizationId={props.organizationId}
            category={props.category}
            windowId={windowId}
          />
        );
      
      case 'customer-group':
        return (
          <CustomerGroupModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            onSuccess={handleSuccess}
            organizationId={props.organizationId}
            group={props.group}
            windowId={windowId}
          />
        );
      
      case 'vendor-group':
        return (
          <VendorGroupModal 
            open={true}
            isMinimized={isMinimized}
            onClose={handleActualClose}
            onMinimize={handleMinimizeOnClose}
            onSuccess={handleSuccess}
            organizationId={props.organizationId}
            group={props.group}
            windowId={windowId}
          />
        );
      
      default:
        return null;
    }
  };

  // Render ALL windows - the modals handle their own visibility via isMinimized prop
  if (windows.length === 0) return null;

  return (
    <Suspense fallback={<LoadingFallback />}>
      {windows.map((window) => (
        <div key={window.id} style={{ zIndex: window.zIndex }}>
          {renderWindowContent(window.id, window.type, window.props, window.isMinimized)}
        </div>
      ))}
    </Suspense>
  );
};
